import React, { CSSProperties, useMemo } from 'react';
import { classNames } from '@fullstory/util/fmt';
import css from './index.module.scss';
import { noop } from 'lodash';
import { defaultRankIndicatorDiameter } from './constants';
import { SimpleRect } from '@fullstory/util/rect';

interface RankIndicatorProps {
  rank: number;
  selected: boolean;
  onSelect: (rank: number) => void;
  playbackScaleFactor: number;
  clientRect: SimpleRect;
  hovering: boolean;
  setHovering: (rank: number, isHovering: boolean) => void;
  zIndex?: number;
  layoutViewport: SimpleRect;
  styleOverrides?: CSSProperties;
}

export const RankIndicator: React.FC<RankIndicatorProps> = ({
  rank,
  clientRect,
  selected,
  onSelect,
  playbackScaleFactor,
  hovering,
  setHovering,
  zIndex,
  layoutViewport,
  styleOverrides,
}) => {
  const positionDetails = useMemo(() => {
    if (selected) {
      const renderScale = 1 / playbackScaleFactor;
      const { innerHeight } = window;

      if (clientRect.bottom * renderScale + defaultRankIndicatorDiameter / 2 < layoutViewport.top) {
        const adjustedClientRect: SimpleRect = { ...clientRect };
        adjustedClientRect.top = (layoutViewport.top + 3) * defaultRankIndicatorDiameter;
        adjustedClientRect.bottom = adjustedClientRect.top + adjustedClientRect.height;
        return {
          offscreen: true,
          above: true,
          below: false,
          adjustedClientRect,
        };
      } else if (clientRect.top * renderScale > innerHeight) {
        const adjusted: SimpleRect = { ...clientRect };
        adjusted.top = layoutViewport.bottom - (adjusted.height + 3 * defaultRankIndicatorDiameter);

        return {
          offscreen: true,
          below: true,
          above: false,
          adjustedClientRect: adjusted,
        };
      }
    }

    return {
      offscreen: false,
      below: false,
      above: false,
      adjustedClientRect: clientRect,
    };
  }, [clientRect, layoutViewport.bottom, layoutViewport.top, playbackScaleFactor, selected]);

  return (
    <div
      data-component="RankIndicator"
      className={classNames(css.rankIndicator, {
        [css.selected]: selected,
        [css.hover]: hovering,
        [css.offscreen]: positionDetails.offscreen,
        [css.above]: positionDetails.above,
        [css.below]: positionDetails.below,
      })}
      onClick={() => onSelect(rank)}
      onKeyDown={noop}
      onBlur={noop}
      role="button"
      tabIndex={rank}
      style={{
        transform: `translateY(0px) scale(${playbackScaleFactor}, ${playbackScaleFactor})`,
        left: positionDetails.adjustedClientRect.left,
        top: positionDetails.adjustedClientRect.top,
        zIndex,
      }}
      onMouseMove={() => setHovering(rank, true)}
      onMouseOut={() => setHovering(rank, false)}
    >
      <div className={css.content} style={styleOverrides}>
        {rank}
      </div>
    </div>
  );
};
